import React, { useState, useRef, useEffect } from 'react';
import { Container, Nav, Navbar, Form, Modal, CloseButton } from 'react-bootstrap';
import { ClockHistory, Search, Person, QuestionCircle, CalendarHeart, List, Wallet2, PlayCircle, ChatDots, Bag, Star, StarFill } from 'react-bootstrap-icons';
import { useTheme } from '../ThemeContext';
import { useAuth } from './AuthContext';
import { useLocation, NavLink, Link } from 'react-router-dom';
import SalesFeed from './SalesFeed';
import { debounce } from 'lodash';
import { useFilter } from './FilterContext';

const customStyles = (theme) => ({
  navbar: {
    height: '56px',
    left: '10px',
    right: '10px',
    bottom: '10px',
    borderRadius: '50px',
    boxShadow: theme === 'dark'
      ? '0 16px 34px rgba(0,0,0,0.38)'
      : '0 14px 30px rgba(28, 54, 84, 0.2)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
    border: `1px solid ${theme === 'dark' ? 'rgba(202, 222, 241, 0.2)' : 'rgba(255, 255, 255, 0.62)'}`,
    background: theme === 'dark' ? 'rgba(34, 49, 64, 0.64)' : 'rgba(255, 255, 255, 0.62)',
    padding: '0 6px',
  },
  icon: {
    transition: 'all 0.2s ease',
    width: '42px',
    height: '42px',
    padding: 0,
    borderRadius: '50%',
    position: 'relative',
    color: theme === 'dark' ? '#e9f2fb' : '#5f7184',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    boxShadow: 'none',
  },
  activeIcon: {
    color: theme === 'dark' ? '#84dd94' : '#3f9860',
    transform: 'scale(1.15)'
  },
  activeIconWallet: {
    color: theme === 'dark' ? '#8dcfff' : '#2f7ed8',
    transform: 'scale(1.15)',
  },
  searchModal: {
    modalContent: {
      maxWidth: '90vw', // Ограничиваем максимальную ширину
      margin: '1rem', // Добавляем отступы от краев экрана
      width: '600px', // Фиксированная ширина для десктопов
      height: 'auto'
    },
    input: {
      fontSize: '1rem', // Уменьшаем размер шрифта
      padding: '10px 14px 10px 2.5rem', // Уменьшаем паддинги
      borderRadius: '25px',
      border: 'none',
    }
  }
});

const BottomNav = () => {
  const { theme } = useTheme();
  const { searchSales } = useAuth();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  const searchResultsAreaRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isWaitingListPage = location.pathname === '/waiting-list'
  const [searchResults, setSearchResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [searchResultsAreaHeight, setSearchResultsAreaHeight] = useState(0);
  const [searchResultsAreaWidth, setSearchResultsAreaWidth] = useState(0);
  const { showFeedbackOnly, setShowFeedbackOnly } = useFilter();
  const activePaths = {
    '/': <List size={26} />,
    '/waiting-list': <ClockHistory size={26} />,
    '/yesterday-sales': <CalendarHeart size={26} />,
    '/profile': <Person size={26} />,
    '/chat': <ChatDots size={26} />,
    '/market': <Bag size={26} />
  };

  const debouncedSearch = useRef(
    debounce(async (text) => {
      if (!text) return;
      const type = /^\d+$/.test(text) ? 'art' : 'name';
      try {
        const data = await searchSales(type, text);
        setSearchResults(data);
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 800)
  ).current;

  const handleSearch = (text) => {
    setSearchText(text);
    if (!String(text || '').trim()) {
      debouncedSearch.cancel();
      setSearchResults([]);
      return;
    }
    debouncedSearch(text);
  };


  const openSearchModal = () => {
    setShowSearchModal(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      debouncedSearch.flush();
    }
  };
  const styles = customStyles(theme);
  const hasSearchResults = Array.isArray(searchResults) && searchResults.length > 0;
  const hasSearchQuery = Boolean(String(searchText || '').trim());
  const searchContentModeClass = hasSearchResults ? 'app-bottom-nav-search-content--has-results' : 'app-bottom-nav-search-content--compact';
  const searchHeadOverlayInset = hasSearchResults ? 62 : 0;

  useEffect(() => {
    if (!showSearchModal || !hasSearchResults) {
      setSearchResultsAreaHeight(0);
      setSearchResultsAreaWidth(0);
      return undefined;
    }

    const node = searchResultsAreaRef.current;
    if (!node) return undefined;

    let rafId = 0;
    const updateMetrics = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setSearchResultsAreaHeight(Math.max(0, Math.floor(node.clientHeight)));
        setSearchResultsAreaWidth(Math.max(0, Math.floor(node.clientWidth)));
      });
    };

    updateMetrics();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateMetrics);
      resizeObserver.observe(node);
    }

    const viewport = window.visualViewport;
    window.addEventListener('resize', updateMetrics);
    viewport?.addEventListener('resize', updateMetrics);
    viewport?.addEventListener('scroll', updateMetrics);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMetrics);
      viewport?.removeEventListener('resize', updateMetrics);
      viewport?.removeEventListener('scroll', updateMetrics);
    };
  }, [hasSearchResults, showSearchModal]);


  useEffect(() => {

    const items = [];
    if (!isHomePage) items.push({ path: '/', icon: <List />, label: 'Главная' });
    if (!isWaitingListPage) items.push({ path: '/waiting-list', icon: <ClockHistory />, label: 'Лист ожидания' });
    if (location.pathname !== '/chat') items.push({ path: '/chat', icon: <ChatDots />, label: 'Чат' });
    if (location.pathname !== '/market') items.push({ path: '/market', icon: <Bag />, label: 'Барахолка' });
    if (location.pathname !== '/my-market-items') items.push({ path: '/my-market-items', icon: <Bag />, label: 'Мои товары' });
    items.push(
      { path: '/yesterday-sales', icon: <CalendarHeart />, label: 'Вчерашние скидки' },
      { path: '/profile', icon: <Person />, label: 'Профиль' }
    );
    setMenuItems(items);
  }, [location.pathname]);

  const menuModalStyles = {
    item: {
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      color: theme === 'dark' ? '#e6eff9' : '#2f4154',
      transition: 'background 0.2s',
      cursor: 'pointer',
      borderRadius: '14px',
    }
  };

  return (
    <>
      <Navbar
        fixed="bottom"
        style={styles.navbar}
        className={`navbar app-bottom-nav ${theme === 'dark' ? 'app-bottom-nav--dark' : 'app-bottom-nav--light'}`}
      >
        <Container fluid className="px-3">
          <Nav className="d-flex justify-content-between w-100 align-items-center">


            {/* Кнопка меню */}
            <Nav.Link
              onClick={() => setShowMenuModal(prev => !prev)}
              style={styles.icon}
              className={`app-bottom-nav__item ${theme === 'dark' ? 'app-bottom-nav__item--dark' : 'app-bottom-nav__item--light'}`}
            >
              <List size={26} />
            </Nav.Link>

            {/* Поиск только на главной */}
            {isHomePage && (
              <Nav.Link
                onClick={openSearchModal}
                style={styles.icon}
                className={`app-bottom-nav__item ${theme === 'dark' ? 'app-bottom-nav__item--dark' : 'app-bottom-nav__item--light'}`}
              >
                <Search size={26} />
              </Nav.Link>
            )}
            {isHomePage && (
              <Nav.Link
                onClick={() => setShowFeedbackOnly(!showFeedbackOnly)}
                style={{
                  ...styles.icon,
                  ...(showFeedbackOnly ? styles.activeIconWallet : {}),
                }}
                className={`app-bottom-nav__item ${theme === 'dark' ? 'app-bottom-nav__item--dark' : 'app-bottom-nav__item--light'} ${showFeedbackOnly ? 'app-bottom-nav__item--wallet-active' : ''}`}
              >
                <Wallet2 size={26} />
              </Nav.Link>
            )}
            {/* Иконка информации */}
            {(isHomePage || isWaitingListPage) && (
              <Nav.Link
                onClick={() => setShowInfoModal(true)}
                style={styles.icon}
                className={`app-bottom-nav__item ${theme === 'dark' ? 'app-bottom-nav__item--dark' : 'app-bottom-nav__item--light'}`}
              >
                <QuestionCircle size={26} />
              </Nav.Link>
            )}
          </Nav>
        </Container>
      </Navbar>

      <Modal
        show={showMenuModal}
        onHide={() => setShowMenuModal(false)}
        animation={true}
        backdropClassName="menu-backdrop"
        dialogClassName="menu-modal"
        centered={false}
        transition={null} // Отключаем стандартную анимацию
        enforceFocus={false}
      >
        <Modal.Body
          className={`app-menu-modal-body ${theme === 'dark' ? 'app-menu-modal-body--dark' : 'app-menu-modal-body--light'}`}
          style={{
            padding: '0',
            backgroundColor: 'transparent',
            borderRadius: '15px'
          }}
        >
          {menuItems.map((item) => (
            <Nav.Link
              key={item.path}
              as={NavLink}
              to={item.path}
              style={menuModalStyles.item}
              className={`app-menu-modal-item ${theme === 'dark' ? 'app-menu-modal-item--dark' : 'app-menu-modal-item--light'}`}
              onClick={() => setShowMenuModal(false)}
            >
              {React.cloneElement(item.icon, { size: 20 })}
              {item.label}
            </Nav.Link>
          ))}
        </Modal.Body>
      </Modal>

      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered={false}
        dialogClassName="app-bottom-nav-search-dialog"
        className={`${theme === 'dark' ? 'text-white modal-dark' : 'text-dark modal-light'} app-bottom-nav-search`}
        backdropClassName="search-backdrop"
        contentClassName={`overflow-hidden app-bottom-nav-search-content ${searchContentModeClass} ${theme === 'dark' ? 'app-bottom-nav-search-content--dark' : 'app-bottom-nav-search-content--light'}`}
      >
        <div
          className={`app-bottom-nav-search-shell ${hasSearchResults ? 'app-bottom-nav-search-shell--with-results' : ''}`}
          style={{ '--search-head-height': `${searchHeadOverlayInset}px` }}
        >
          <div className="app-bottom-nav-search-head">
            <div className="position-relative flex-grow-1 app-bottom-nav-search-input-wrap">
              <Form.Control
                ref={searchInputRef}
                type="search"
                placeholder="Артикул или название..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className={`app-bottom-nav-search-input ${theme === 'dark' ? 'app-bottom-nav-search-input--dark' : 'app-bottom-nav-search-input--light'}`}
                style={styles.searchModal.input}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                className="position-absolute"
                style={{
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fill: theme === 'dark' ? '#adb5bd' : '#6c757d',
                  pointerEvents: 'none'
                }}
                viewBox="0 0 24 24"
              >
                <path d="M10 18a7.952 7.952 0 0 0 4.897-1.688l4.396 4.396 1.414-1.414-4.396-4.396A7.952 7.952 0 0 0 18 10c0-4.411-3.589-8-8-8s-8 3.589-8 8 3.589 8 8 8zm0-14c3.309 0 6 2.691 6 6s-2.691 6-6 6-6-2.691-6-6 2.691-6 6-6z" />
              </svg>
              <button
                type="button"
                className={`app-bottom-nav-search-close-btn ${theme === 'dark' ? 'app-bottom-nav-search-close-btn--dark' : 'app-bottom-nav-search-close-btn--light'}`}
                onClick={() => setShowSearchModal(false)}
                aria-label="Закрыть поиск"
              >
                ×
              </button>
            </div>
          </div>

          <div
            ref={searchResultsAreaRef}
            className={`app-bottom-nav-search-results ${hasSearchResults ? 'app-bottom-nav-search-results--has-items' : 'app-bottom-nav-search-results--empty'}`}
          >
            {hasSearchResults ? (
              <div className="app-bottom-nav-search-feed">
                <SalesFeed
                  sales={searchResults}
                  type="modal"
                  modalGridHeight={searchResultsAreaHeight}
                  modalGridWidth={searchResultsAreaWidth}
                  modalTopInset={searchHeadOverlayInset}
                />
              </div>

            ) : hasSearchQuery && (
              <div className="text-center py-2 px-2">
                <div style={{ color: theme === 'dark' ? '#fff' : '#666' }}>
                  Ничего не найдено
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        className={`${theme === 'dark' ? 'text-white modal-dark' : 'text-dark modal-light'} app-bottom-nav-info`}
        contentClassName={`rounded-lg shadow app-bottom-nav-info-content ${theme === 'dark' ? 'app-bottom-nav-info-content--dark' : 'app-bottom-nav-info-content--light'}`}
        size="lg"
      >
        <Modal.Header className="border-0 app-bottom-nav-info-header">
          <Modal.Title className="app-info-title">
            <QuestionCircle size={22} />
            Описание элементов
          </Modal.Title>
          <CloseButton
            onClick={() => setShowInfoModal(false)}
            className={theme === 'dark' ? 'btn-close-white' : 'btn-close-black'}
          />
        </Modal.Header>
        <Modal.Body className={`scrollable app-bottom-nav-info-body ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
          <div className="sb-inline-cta app-info-video-link-wrap">
            <Link to="/videos" className="sb-mini-link sb-mini-link--video app-info-video-link" aria-label="Видеоинструкция">
              <PlayCircle size={16} /> Видеоинструкция
            </Link>
          </div>

          {isHomePage && (
            <div className="app-info-layout app-info-sample">
              <section className="app-info-section">
                <h5 className="app-info-section-title">📦 Остатки на складе</h5>
                <div className="app-info-item">
                  <div className="app-info-chip-stack">
                    <span className="stock-badge green app-info-stock-chip">36/36</span>
                    <span className="stock-badge orange app-info-stock-chip">8/8</span>
                  </div>
                  <div className="app-info-item-text">
                    <div><span className="app-info-dot app-info-dot--green" /> Зеленый — склад Wildberries.</div>
                    <div><span className="app-info-dot app-info-dot--orange" /> Оранжевый — склад продавца.</div>
                  </div>
                </div>
              </section>

              <section className="app-info-section">
                <h5 className="app-info-section-title">📏 Размеры</h5>
                <div className="app-info-item">
                  <div className="sizes-scroll app-info-sizes-demo">
                    <span className="size-tag size-tag--green">42</span>
                    <span className="size-tag size-tag--green">44</span>
                    <span className="size-tag size-tag--orange">46</span>
                    <span className="size-tag size-tag--orange">48</span>
                  </div>
                  <p className="app-info-item-text">
                    Цвета такие же, как у остатков. Если размеров много, блок прокручивается.
                  </p>
                </div>
              </section>

              <section className="app-info-section">
                <h5 className="app-info-section-title">🤖 AI прогноз</h5>
                <div className="app-info-inline-samples">
                  <span className="app-info-ai-chip app-info-ai-chip--same">AI: 58%</span>
                  <span className="app-info-ai-chip app-info-ai-chip--down">AI: 41%</span>
                  <span className="app-info-ai-chip app-info-ai-chip--up">AI: 74%</span>
                </div>
                <p className="app-info-item-text">
                  Процент вероятности выкупа по модели ИИ.
                </p>
              </section>

              <section className="app-info-section">
                <h5 className="app-info-section-title">💰 Цена и диапазон</h5>
                <div className="app-info-legend">
                  <div className="app-info-legend-row">
                    <span className="price-text app-info-price-text app-info-price-text--same">2 990₽</span>
                    <span className="app-info-legend-text">Зеленый — цена не изменилась.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <span className="price-text app-info-price-text app-info-price-text--down">2 690₽</span>
                    <span className="app-info-legend-text">Синий — цена снизилась.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <span className="price-text app-info-price-text app-info-price-text--up">3 150₽</span>
                    <span className="app-info-legend-text">Красный — цена повысилась.</span>
                  </div>
                </div>
                <div className="app-info-legend mt-2">
                  <div className="app-info-legend-row">
                    <span className="diapaz-text app-info-diapaz-chip">1 990₽ - 3 450₽</span>
                    <span className="app-info-legend-text">Диапазон цен по истории.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <span className="diapaz-text no-diapaz app-info-diapaz-chip">Нет диапазона</span>
                    <span className="app-info-legend-text">Для товара пока нет данных диапазона.</span>
                  </div>
                </div>
              </section>

              <section className="app-info-section">
                <h5 className="app-info-section-title">💬 Рубли за отзыв и выгода</h5>
                <div className="app-info-legend">
                  <div className="app-info-legend-row">
                    <div className="feedbackpoints-mod feedbackpoints-mod--same">
                      <Wallet2 size={14} color="white" className="feedbackpoints-mod__icon" />
                      <span className="feedbackpoints-mod__current feedbackpoints-mod__current--same">+200₽</span>
                    </div>
                    <span className="app-info-legend-text">Сумма рублей за отзыв без изменений.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <div className="feedbackpoints-mod feedbackpoints-mod--increased">
                      <Wallet2 size={14} color="white" className="feedbackpoints-mod__icon" />
                      <span className="feedbackpoints-mod__previous">+120₽</span>
                      <span className="feedbackpoints-mod__current feedbackpoints-mod__current--increased">+200₽</span>
                    </div>
                    <span className="app-info-legend-text">Сумма изменилась после попадания товара в ленту.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <div className="feedbackpoints-mod feedbackpoints-mod--removed">
                      <Wallet2 size={14} color="white" className="feedbackpoints-mod__icon" />
                      <span className="feedbackpoints-mod__removed">200₽</span>
                    </div>
                    <span className="app-info-legend-text">Это означает, что плашка «рубли за отзыв» пропала.</span>
                  </div>
                </div>
                <div className="app-info-legend mt-2">
                  <div className="app-info-legend-row">
                    <div className="vigoda vigoda--same">-80 (27%)</div>
                    <span className="app-info-legend-text">Число с минусом — итоговая сумма, которую вы заплатите.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <div className="vigoda vigoda--decreased"><span className="vigoda__previous">+130 (19%)</span>+90 (14%)</div>
                    <span className="app-info-legend-text">Число с плюсом — ваш доход.</span>
                  </div>
                  <div className="app-info-legend-row">
                    <div className="vigoda vigoda--increased"><span className="vigoda__previous">-120 (34%)</span>-80 (23%)</div>
                    <span className="app-info-legend-text">Если расчет изменился, отображаются старый и новый варианты.</span>
                  </div>
                </div>
                <div className="app-info-item-text">
                  <p className="mb-0">Процент (%) показывает долю выгоды или переплаты.</p>
                </div>
              </section>

              <section className="app-info-section">
                <h5 className="app-info-section-title">⋯ Остальные метки</h5>
                <div className="app-info-inline-samples">
                  <div className={`market-type-pill ${theme === 'dark' ? 'market-type-pill--dark' : 'market-type-pill--light'} app-info-market-pill app-info-market-pill--wb`}>
                    <span className="market-type-pill__label">WB</span>
                  </div>
                  <div className={`market-type-pill ${theme === 'dark' ? 'market-type-pill--dark' : 'market-type-pill--light'} app-info-market-pill app-info-market-pill--ozon`}>
                    <span className="market-type-pill__label">OZON</span>
                  </div>
                  <div className={`rating-pill ${theme === 'dark' ? 'rating-pill--dark' : 'rating-pill--light'} app-info-rating-pill`}>
                    <div className="rating-pill__stars">
                      <StarFill size={14} color="orange" />
                      <StarFill size={14} color="orange" />
                      <StarFill size={14} color="orange" />
                      <StarFill size={14} color="orange" />
                      <Star size={14} color="orange" />
                    </div>
                    <span className="rating-pill__count"> | 356</span>
                  </div>
                </div>
                <div className="app-info-inline-samples">
                  <div className="app-info-unrefusable-wrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" className="app-info-unrefusable-icon" viewBox="0 0 19 18">
                      <path fill="inherit" fillRule="evenodd" d="M10.6 5.1a.9.9 0 0 0-.9.9v3.2a.9.9 0 1 0 1.8 0V6a.9.9 0 0 0-.9-.9Zm0 7.8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" clipRule="evenodd" />
                      <path fill="inherit" fillRule="evenodd" d="M10.072 2.18a6.703 6.703 0 0 0-3.653 1.455.974.974 0 0 1-.134.09c-.341.191-.774.163-1.057-.12-.322-.323-.327-.851.021-1.144a8.378 8.378 0 0 1 .342-.273A8.326 8.326 0 0 1 9.941.527a8.312 8.312 0 0 1 5.62 1.61 8.508 8.508 0 0 1 3.22 4.935 8.6 8.6 0 0 1-.737 5.869 8.43 8.43 0 0 1-4.339 3.96 8.289 8.289 0 0 1-5.838.132 8.415 8.415 0 0 1-4.51-3.758A8.584 8.584 0 0 1 2.27 8.067l-.856.744a.831.831 0 0 1-1.186-.037.858.858 0 0 1 .036-1.201l2.453-2.338a.833.833 0 0 1 1.223.079c.05.063.092.116.132.17.081.104.162.209.322.389l1.718 2.114a.858.858 0 0 1-.115 1.197.832.832 0 0 1-1.18-.116L3.931 7.98a6.917 6.917 0 0 0 .84 4.46 6.774 6.774 0 0 0 3.63 3.026 6.672 6.672 0 0 0 4.7-.107 6.785 6.785 0 0 0 3.492-3.187c.75-1.452.96-3.128.593-4.724a6.848 6.848 0 0 0-2.593-3.972 6.69 6.69 0 0 0-4.522-1.296Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="app-info-filter-pill">
                    <Wallet2 size={18} />
                  </div>
                </div>
                <p className="app-info-item-text">
                  Красная иконка — невозвратный товар. Кнопка с кошельком в меню фильтрует товары с рублями за отзыв.
                </p>
              </section>
            </div>
          )}

          {isWaitingListPage && (
            <div className="app-info-layout app-info-waiting-layout">
              <section className="app-info-section">
                <h5 className="app-info-section-title">🛒 Лист отслеживания</h5>
                <div className="app-info-step-list">
                  <div className="app-info-step">
                    <span className="app-info-step-num">1</span>
                    <div>
                      <strong>Добавление товара</strong>
                      <p className="mb-0">
                        Введите артикул Wildberries и откройте настройку отслеживания для этого товара.
                      </p>
                    </div>
                  </div>
                  <div className="app-info-step">
                    <span className="app-info-step-num">2</span>
                    <div>
                      <strong>Выбор размеров и складов</strong>
                      <div className="sizes-scroll app-info-sizes-demo mt-2">
                        <span className="size-tag size-tag--green">42</span>
                        <span className="size-tag size-tag--green">44</span>
                        <span className="size-tag size-tag--orange">46</span>
                      </div>
                      <p className="mb-1">Зеленый — склад WB, желтый/оранжевый — склад продавца.</p>
                      <p className="mb-0">
                        Если на разные размеры разные цены, отслеживание цены и условий идет отдельно по каждому выбранному размеру.
                      </p>
                    </div>
                  </div>
                  <div className="app-info-step">
                    <span className="app-info-step-num">3</span>
                    <div>
                      <strong>Желаемая цена</strong>
                      <div className="app-info-inline-samples mt-2">
                        <span className="price-text app-info-price-text app-info-price-text--down">2 390₽</span>
                      </div>
                      <p className="mb-1">Если цена достигнет указанного значения (например, 2 390₽), придет уведомление.</p>
                      <p className="mb-0">После достижения цели товар автоматически ставится на паузу.</p>
                    </div>
                  </div>
                  <div className="app-info-step">
                    <span className="app-info-step-num">4</span>
                    <div>
                      <strong>Уведомления</strong>
                      <p className="mb-0">
                        Первый чекбокс определяет, отправлять ли уведомления при снижении цены.
                      </p>
                    </div>
                  </div>
                  <div className="app-info-step">
                    <span className="app-info-step-num">5</span>
                    <div>
                      <strong>Диапазон цен</strong>
                      <div className="app-info-inline-samples mt-2">
                        <span className="diapaz-text app-info-diapaz-chip">1 990₽ - 3 450₽</span>
                      </div>
                      <p className="mb-0">
                        Показывает реальный диапазон цен на основе исторических данных. Всю историю изменения цены можно открыть из диапазона.
                      </p>
                    </div>
                  </div>
                  <div className="app-info-step">
                    <span className="app-info-step-num">6</span>
                    <div>
                      <strong>Дата последнего обновления</strong>
                      <p className="mb-0">
                        Вверху справа отображается дата последнего обновления в формате день.месяц.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="app-info-note">
                  <p className="mb-1">
                    Это позволяет отслеживать только нужные товары и не пропускать выгодную цену.
                  </p>
                  <p className="mb-1">
                    Пример: если ждете скидку на кроссовки, добавьте артикул, выберите размер и задайте желаемую цену — приложение уведомит, когда цена снизится.
                  </p>
                  <p className="mb-0">
                    Можно включить уведомления на снижение цены для любых категорий: техника, электроника и другие товары.
                  </p>
                </div>
              </section>
            </div>
          )}

          {!isHomePage && !isWaitingListPage && (
            <div className="app-info-section">
              <p>Описание недоступно для этой страницы.</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BottomNav;

